import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
// ⚠️ Deep-imports the DESKTOP variants on purpose (ledger hazards 6 and 16): `arrayToMap` exists
// in BOTH variants but DIFFERS (web returns null for empty input, mobile returns {}), and
// `userLogout`/`supportLogout` are web-only. `getChannelData` is now in `shared.ts` — identical
// on both platforms — so it alone would not require naming a variant.
import { arrayToMap, getChannelData } from "../../../helpers/catalogueHelpers";
import { convertArrayToObject } from "../../../helpers/commonHelpers";
import { CATEGORY_TYPE } from "../../../data/constants";
import { supportLogout, userLogout } from "../LoginSlice";
import {
  sharedInitialState,
  sharedReducers,
  type SharedCatalogState,
} from "./shared";

export interface CatalogState extends SharedCatalogState {
  // These three are redeclared verbatim from apps/web so the surface `--verify` compares is
  // byte-for-byte the app's. `shared.ts` holds them widened purely so both platforms can.
  posSettings: {};
  tagsPriorityArr: [];
  scheduleData: [];
  optionSetData: [];
  mostSoldItems: [];
  mostSoldLastFetchingTime: {};
  selectedDeal: [];
  newChannelItemMap: {};
}

const initialState: CatalogState = {
  ...sharedInitialState,
  // Web starts `{}`; mobile seeds `{ channelvos: [] }`. Differing default, preserved (hazard 7).
  categoryData: {},
  // Re-supplied after the spread because web narrows these to the empty tuple `[]` while
  // `sharedInitialState` types them `any[]`. Same values as the app; the narrowing is web's.
  tagsPriorityArr: [],
  scheduleData: [],
  optionSetData: [],
  selectedDeal: [],
  mostSoldLastFetchingTime: {},
  mostSoldItems: [],
  newChannelItemMap: {},
};

export const catalogueSlice = createSlice({
  name: "catalogue",
  initialState,
  reducers: {
    ...sharedReducers,
    // DIVERGENT: web derives the whole catalogue inline here. Mobile fetches these through ~24
    // per-section reducers instead and its version only handles categories + schedule.
    getCatalogueDataSuccess: (state: CatalogState, action: PayloadAction<any>) => {
      state.catalogueData = action.payload;
      const categories = (action.payload?.categories || []) as Array<{ categoryTypes?: string[] }>;

      const catalogTypes: any[] = [];
      const inventoryTypes: any[] = [];

      categories.forEach((category) => {
        const types = category?.categoryTypes || [];
        if (types.includes(CATEGORY_TYPE.CATALOG)) {
          catalogTypes.push(category);
        }
        if (types.includes(CATEGORY_TYPE.INVENTORY)) {
          inventoryTypes.push(category);
        }
      });

      state.categoriesWithCatalogType = catalogTypes;
      state.categoriesWithInventoryType = inventoryTypes;

      state.categoryData = convertArrayToObject(action.payload?.categories || [], 'mid');
      state.catalogOnlyCategoryData = convertArrayToObject(catalogTypes, 'mid');
      state.channelData = getChannelData(action.payload);
      state.scheduleData = action.payload?.itemSchedules || [];
      state.charges = arrayToMap(action.payload?.charges || [], 'mid');
      state.taxTypes = action.payload?.taxTypes || [];
      state.taxArea = action.payload?.taxAreas[0] || {};
      state.categoryGroup = action.payload?.categoryGroups || [];
      state.brandsData = action.payload?.brands || [];
      state.channelvos = action.payload?.channelvos || [];
      state.channelDef = action.payload?.channelDefs || [];
      state.isCatalogueFetching = false;

    },
    // DIVERGENT: web keeps the raw array; mobile stores arrayToMap(..., 'mid').
    getOptionSetsDataSuccess: (state: CatalogState, action: PayloadAction<any>) => {
      state.optionSetData = action.payload.response || [];
      state.isOptionSetFetching = false;
    },
    optionSetsDataCallComplete: (state: CatalogState) => {
      // this action is to tell that the option sets data call has completed
    },
    getMostSoldItems: (state: CatalogState, actions: PayloadAction<any>) => {},
    saveMostSoldItems: (state: CatalogState, actions: PayloadAction<any>) => {
      state.mostSoldLastFetchingTime = new Date();
      state.mostSoldItems = actions.payload;
    },
    mostSoldItemsCallComplete: (state: CatalogState) => {
      // this action is to tell that the most sold items call has completed
    },
    getSelectedDealData: (state: CatalogState, action: PayloadAction<any>) => {
      state.selectedDeal = action.payload;
    },
    posSettingsCallComplete: (state: CatalogState) => {
      // this action is to tell that the POS settings call has completed
    },
    discountsDataCallComplete: (state: CatalogState) => {
      // this action is to tell that the discounts data call has completed
    },
    chargesDataCallComplete: (state: CatalogState) => {
      // this action is to tell that the charges data call has completed
    },
    taxAreaDataCallComplete: (state: CatalogState) => {
      // this action is to tell that the tax area data call has completed
    },
    taxTypesDataCallComplete: (state: CatalogState) => {
      // this action is to tell that the tax types data call has completed
    },
    channelDefDataCallComplete: (state: CatalogState) => {
      // this action is to tell that the channel def data call has completed
    },
    channelvosDataCallComplete: (state: CatalogState) => {
      // this action is to tell that the channelvos data call has completed
    },
    categoryGroupDataCallComplete: (state: CatalogState) => {
      // this action is to tell that the category group data call has completed
    },
    brandsDataCallComplete: (state: CatalogState) => {
      // this action is to tell that the brands data call has completed
    },
    bizBrandsDataCallComplete: (state: CatalogState) => {
      // this action is to tell that the biz brands data call has completed
    },
    // DIVERGENT by a `!!` only — mobile omits it. Same runtime result; preserved verbatim.
    getBizBrandsDataFail: (state: CatalogState) => {
      state.isBizBrandsFetching = false;
      state.bizBrandsData = !!state.bizBrandsData?.length ? state.bizBrandsData : [];
    },
    // DIVERGENT: mobile also sets `tagsPriorityArr` from setPriorityForItemTags.
    getItemTagSuccess: (state: CatalogState, action: PayloadAction<any>) => {
      state.isFetchingItemTag = false;
      state.itemTags = arrayToMap(action.payload.response, "mid");
    },
    itemTagCallComplete: (state: CatalogState) => {
      // this action is to tell that the item tag data call has completed
    },
    updateCatalogSlice: (state: CatalogState, action: PayloadAction<any>) => {
      return action.payload;
    },
    channelItemMapDetail: (state: CatalogState, action: PayloadAction<any>) => {
      state.newChannelItemMap = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLogout, () => initialState)
      .addCase(supportLogout, () => initialState);
  },
});

export const {
  getCatalogueData,
  getCatalogueDataSuccess,
  getCatalogueDataFail,
  setChannelDataSuccess,
  setCatagoryDataSuccess,
  setMenuDataSuccess,
  getOptionSetsData,
  getOptionSetsDataSuccess,
  getOptionSetsDataFail,
  getPOSSettingsData,
  getMostSoldItems,
  saveMostSoldItems,
  getPOSSettingsDataSuccess,
  getPOSSettingsDataFail,
  getDiscountsData,
  getDiscountsDataSuccess,
  getDiscountsDataFail,
  getSelectedDealData,
  getBizBrandsData,
  getBizBrandsDataSuccess,
  getBizBrandsDataFail,
  updateCurrentSelectedMode,
  getItemTag,
  getItemTagSuccess,
  storeItemTagsPriority,
  updateCatalogueData,
  updateCatalogSlice,
  setMenuToItemDataMap,
  channelItemMapDetail,
  posSettingsCallComplete,
  catalogueDataCallComplete,
  channelvosDataCallComplete,
  channelDefDataCallComplete,
  optionSetsDataCallComplete,
  mostSoldItemsCallComplete,
  discountsDataCallComplete,
  chargesDataCallComplete,
  taxAreaDataCallComplete,
  taxTypesDataCallComplete,
  categoryGroupDataCallComplete,
  bizBrandsDataCallComplete,
  itemTagCallComplete,
  brandsDataCallComplete,
} = catalogueSlice.actions;

export default catalogueSlice.reducer;
